// The No matches / Several matches review (CAL-5998), as the panel reads it: an
// employee of those files is "merged" once the Matches file lists it, "no match"
// once a reviewer marked it, and "to review" otherwise. Whether the bot then
// uploaded a merged employee's documents is the task's Logs' business. Pure
// helpers; the JSX is in UnmatchedReview.tsx.
import {
  MERGE_REPORT_KINDS,
  type MergeReportKind,
  type UnmatchedEmployeeStatus,
  type UnmatchedKindSummary,
  type UnmatchedReview,
  type UnmatchedReviewAction,
  type UnmatchedReviewMarkResponse,
} from "features/adminUsers/adminUsers";
import { formatApiError } from "utils/formatApiError";

import { countersTimeAgo } from "./employeeCounters";
import type { MetaTone } from "./MetaPill";

export const MATCHES_FILENAME = "s3_to_site_names_matches.csv";
export const UNMATCHED_REVIEW_FILES: Record<MergeReportKind, string> = {
  no_matches: "s3_to_site_names_no_matches.csv",
  several_matches: "s3_to_site_names_several_matches.csv",
};
export const UNMATCHED_KIND_LABELS: Record<MergeReportKind, string> = {
  no_matches: "No matches",
  several_matches: "Several matches",
};

/** What to press once merged employees wait for a run - the same fact the Run
 *  merged EEs button is enabled on. After a roster-only run no task has run yet,
 *  the new rows are already split across the tasks and Run all is the button. */
export type RunHint = "run_all" | "run_merged_ees";

export function unmatchedKindForFilename(
  filename: string,
): MergeReportKind | null {
  return (
    MERGE_REPORT_KINDS.find(
      (kind) => UNMATCHED_REVIEW_FILES[kind] === filename,
    ) ?? null
  );
}

export function matchesFileLabel(filename: string): string {
  const kind = unmatchedKindForFilename(filename);
  return kind ? UNMATCHED_KIND_LABELS[kind] : "Matches";
}

export function jobUnmatchedReview(
  job: Record<string, unknown> | null | undefined,
): UnmatchedReview | null {
  const raw = job?.unmatched_review;
  return raw && typeof raw === "object" ? (raw as UnmatchedReview) : null;
}

/** A summary the backend has not recomputed since the states changed says
 *  nothing this panel can read - the next recount rewrites it. */
function isReadable(summary: UnmatchedKindSummary | null | undefined): boolean {
  return typeof summary?.employees === "number";
}

export function plural(count: number, noun: string): string {
  return `${count} ${noun}${count === 1 ? "" : "s"}`;
}

export function formatReviewedAt(iso: string | null | undefined): string {
  if (!iso) return "";
  const ts = new Date(iso);
  return Number.isNaN(ts.getTime()) ? iso : ts.toLocaleString();
}

export function unmatchedReviewSteps(hint: RunHint): string[] {
  return [
    "Download the file.",
    "Find the employees on the vendor site and fill in the employee codes.",
    "Merge reports - write No Match for the ones you could not find.",
    hint === "run_merged_ees"
      ? "Run merged EEs - the tasks that already ran keep their employees."
      : "Run all - the new rows are split across the tasks by themselves.",
  ];
}

export const UNMATCHED_REVIEW_MARK_HINT =
  "When none of the remaining employees can be matched, press No match found on the file.";

function nonZero(...items: Array<[number, string]>): string | null {
  const parts = items
    .filter(([count]) => count > 0)
    .map(([count, text]) => `${count} ${text}`);
  return parts.length ? parts.join(", ") : null;
}

export interface UnmatchedFileLabel {
  label: string;
  tone: MetaTone;
  /** Muted second line under the tag; null when there is nothing to add. */
  detail: string | null;
}

/** The one-line state of a file for the Matches tab; first rule that fits wins. */
export function unmatchedFileLabel(
  summary: UnmatchedKindSummary | null | undefined,
): UnmatchedFileLabel | null {
  if (!summary || !isReadable(summary)) return null;
  if (summary.employees === 0) {
    return { label: "nothing to resolve", tone: "success", detail: null };
  }
  if (summary.pending > 0) {
    return {
      label: `${summary.pending} to review`,
      tone: "warning",
      detail: nonZero(
        [summary.merged, "merged to Matches"],
        [summary.no_match, "no match"],
      ),
    };
  }
  if (summary.merged > 0) {
    return {
      label: `${summary.merged} merged to Matches`,
      tone: "success",
      detail: nonZero([summary.no_match, "no match"]),
    };
  }
  const who = [
    summary.reviewed_by ? `by ${summary.reviewed_by}` : "",
    formatReviewedAt(summary.reviewed_at),
  ].filter(Boolean);
  return {
    label: "reviewed - no match found",
    tone: "success",
    detail: who.length ? who.join(", ") : null,
  };
}

/** The tooltip breakdown of one file. */
export function unmatchedReviewLines(
  kind: MergeReportKind,
  summary: UnmatchedKindSummary,
  rows: number | null | undefined,
): string[] {
  const lines: string[] = [];
  const candidates =
    kind === "several_matches" && rows != null && rows !== summary.employees
      ? ` (${plural(rows, "candidate row")})`
      : "";
  lines.push(
    `${UNMATCHED_KIND_LABELS[kind]}: ${plural(summary.employees, "employee")} in the file${candidates}.`,
  );
  if (summary.pending > 0) {
    lines.push(
      `${summary.pending} to review - not in the Matches file and not marked.`,
    );
  }
  if (summary.merged > 0) {
    lines.push(
      `${summary.merged} merged to the Matches file - the task's Logs say what the bot did with them.`,
    );
  }
  if (summary.no_match > 0) {
    const when = formatReviewedAt(summary.reviewed_at);
    const who = summary.reviewed_by
      ? ` (last by ${summary.reviewed_by}${when ? `, ${when}` : ""})`
      : "";
    lines.push(`${summary.no_match} reviewed - no match found${who}.`);
  }
  if (summary.stale_marks > 0) {
    lines.push(
      `${plural(summary.stale_marks, "mark")} of employees the file no longer lists.`,
    );
  }
  return lines;
}

export interface UnmatchedCardTag {
  kind: MergeReportKind;
  label: string;
  lines: string[];
}

/** The job card names only what somebody has to act on: employees still to review. */
export function unmatchedCardTags(
  review: UnmatchedReview | null,
): UnmatchedCardTag[] {
  if (!review) return [];
  return MERGE_REPORT_KINDS.flatMap((kind) => {
    const summary = review[kind];
    if (!isReadable(summary) || !summary || summary.pending === 0) return [];
    const checked = review.computed_at
      ? `Checked ${countersTimeAgo(review.computed_at)}.`
      : "";
    return [
      {
        kind,
        label: `${UNMATCHED_KIND_LABELS[kind]}: ${summary.pending} to review`,
        lines: [
          ...unmatchedReviewLines(kind, summary, null),
          checked,
          "Click to open Manage job - Matches.",
        ].filter(Boolean),
      },
    ];
  });
}

export type ChipColor =
  | "default"
  | "primary"
  | "secondary"
  | "error"
  | "info"
  | "success"
  | "warning";

export interface EmployeeStateChip {
  label: string;
  color: ChipColor;
  hint: string;
}

/** The Status cell of an employee in the CSV viewer. */
export function employeeStateChip(
  status: UnmatchedEmployeeStatus | null | undefined,
): EmployeeStateChip {
  if (!status) {
    return {
      label: "-",
      color: "default",
      hint: "This employee is not in the review - reload the Matches tab.",
    };
  }
  if (status.state === "pending") {
    return {
      label: "to review",
      color: "warning",
      hint: "Not in the Matches file and not marked.",
    };
  }
  if (status.state === "no_match") {
    const by = status.reviewed_by ? ` by ${status.reviewed_by}` : "";
    const at = status.reviewed_at
      ? ` at ${formatReviewedAt(status.reviewed_at)}`
      : "";
    const via =
      status.reviewed_source === "merge"
        ? " (a No Match row of a merged file)"
        : "";
    return {
      label: "no match",
      color: "default",
      hint: `Reviewed - no match found${by}${at}${via}.`,
    };
  }
  return {
    label: "merged to Matches",
    color: "success",
    hint: "In the Matches file with the employee code shown; the task's Logs say what the bot did.",
  };
}

/** The viewer's footer: rows, and the employees by state. */
export function unmatchedReviewLegend(
  summary: UnmatchedKindSummary | null | undefined,
  rows: number,
): string {
  const base = plural(rows, "row");
  if (!summary) return base;
  const parts = nonZero(
    [summary.pending, "to review"],
    [summary.merged, "merged to Matches"],
    [summary.no_match, "no match"],
  );
  return parts ? `${base} - ${parts}` : base;
}

export function describeReviewOutcome(
  kind: MergeReportKind,
  action: UnmatchedReviewAction,
  res: UnmatchedReviewMarkResponse,
): string {
  const file = `${UNMATCHED_KIND_LABELS[kind]} file`;
  if (action === "clear") {
    const cleared = res.cleared ?? 0;
    const verb = cleared === 1 ? "is" : "are";
    return `No match found undone: ${plural(cleared, "employee")} of the ${file} ${verb} no longer marked as reviewed and ${verb} to review again.`;
  }
  const marked = res.marked ?? 0;
  const skipped = res.skipped_merged
    ? ` ${plural(res.skipped_merged, "employee")} already in the Matches file left as merged.`
    : "";
  return `${plural(marked, "employee")} of the ${file} marked as reviewed - no match found.${skipped}`;
}

const REVIEW_ERROR_TEXTS: Record<string, string> = {
  invalid_kind: "The file is not one of No matches / Several matches.",
  invalid_action: "Unknown action - reload the page and try again.",
  not_upload_job: "Only upload jobs have No matches / Several matches files.",
  file_missing:
    "The file is not in Data Storage any more - reload the Matches tab.",
  review_busy:
    "Another review action is running on this job - try again in a moment.",
  storage_unavailable:
    "Data Storage could not be read or written right now. Try again in a minute.",
  storage_error:
    "Data Storage could not be read or written right now. Try again in a minute.",
};

function errorPayload(error: unknown): Record<string, unknown> | undefined {
  const data = (error as { data?: unknown } | undefined)?.data;
  return data && typeof data === "object"
    ? (data as Record<string, unknown>)
    : undefined;
}

export function describeReviewError(error: unknown): string {
  const payload = errorPayload(error);
  if ((error as { status?: unknown } | undefined)?.status === 403) {
    const reason = payload?.error ?? payload?.detail;
    return typeof reason === "string" && reason
      ? reason
      : "You do not have permission to mark employees as reviewed.";
  }
  const code = payload?.code;
  if (typeof code === "string" && REVIEW_ERROR_TEXTS[code])
    return REVIEW_ERROR_TEXTS[code];
  if (typeof payload?.message === "string" && payload.message)
    return payload.message;
  return formatApiError(payload, "The employees could not be marked.");
}
